import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, fmtError } from "@/lib/api";
import RatingStars from "@/components/RatingStars";

/**
 * Modal de droit de réponse à un avis reçu.
 * - Affiche l'avis original (note + commentaire) en tête, non éditable
 * - Textarea réponse 5-2000 caractères
 * - Une seule réponse possible par avis (le backend écrase si déjà présente,
 *   mais on préfère ne pas encourager cet usage — on affiche donc un
 *   avertissement si `avis.reponse` existe déjà)
 */
const MAX = 2000;
const MIN = 5;

export default function ResponseModal({ avis, onClose, onSubmitted }) {
  const [reponse, setReponse] = useState(avis.reponse || "");
  const [busy, setBusy] = useState(false);
  const isEdit = !!avis.reponse;

  const canSubmit = reponse.trim().length >= MIN && reponse.length <= MAX && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post(`/avis/${avis.id}/reponse`, { reponse: reponse.trim() });
      toast.success(isEdit ? "Réponse mise à jour." : "Réponse publiée.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(fmtError(err));
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="response-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-md shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#E4E7EB]">
          <div>
            <h2 id="response-modal-title" className="text-lg font-semibold text-[#1F2933]">
              {isEdit ? "Modifier votre réponse" : "Répondre à cet avis"}
            </h2>
            <p className="text-xs text-[#3E4C59] mt-0.5">
              Votre réponse sera affichée sous l'avis d'origine, publiquement.
            </p>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="response-modal-close" className="text-[#3E4C59] hover:text-[#1F2933]">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 bg-[#F7F8FA] border-b border-[#E4E7EB]">
          <div className="flex items-center gap-2 mb-2">
            <RatingStars value={avis.note} readOnly size={18} />
            <span className="text-sm font-semibold text-[#1F2933]">{avis.note}/5</span>
            {avis.from_nom && (
              <span className="text-xs text-[#3E4C59]">— {avis.from_nom}</span>
            )}
          </div>
          <p className="text-sm text-[#1F2933] leading-relaxed">{avis.commentaire}</p>
        </div>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <div>
            <label htmlFor="response-text" className="block text-sm font-medium text-[#1F2933] mb-2">
              Votre réponse <span className="text-[#C0392B]">*</span>
              <span className="text-xs text-[#3E4C59] font-normal ml-2">
                (min {MIN}, max {MAX} caractères)
              </span>
            </label>
            <textarea
              id="response-text"
              rows={5}
              value={reponse}
              onChange={(e) => setReponse(e.target.value.slice(0, MAX))}
              placeholder="Répondez calmement et factuellement. Une réponse professionnelle rassure les futurs clients."
              data-testid="response-modal-text"
              className="w-full border border-[#CBD5E1] rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B3A5C] resize-y"
            />
            <div className={`text-xs mt-1 ${reponse.length < MIN ? "text-[#C0392B]" : "text-[#3E4C59]"}`}>
              {reponse.length} / {MAX}
              {reponse.length > 0 && reponse.length < MIN && ` — encore ${MIN - reponse.length} caractère(s) minimum`}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E4E7EB]">
            <button type="button" onClick={onClose} data-testid="response-modal-cancel" className="btn-ghost">
              Annuler
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="response-modal-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? "Envoi…" : isEdit ? "Mettre à jour" : "Publier la réponse"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
